<script setup>
import { LControlLayers, LGeoJson, LMap, LTileLayer, LImageOverlay, LMarker } from "@vue-leaflet/vue-leaflet"
import "leaflet/dist/leaflet.css"
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import LFullScreen from "vue-leaflet-fullscreen"
import "vue-leaflet-fullscreen/dist/style.css"
import axios from '@/helpers/axios'
import Loader from '@/views/Loader.vue'
import moment from "moment";
import Breadcrumbs from '@/views/general/Breadcrumbs.vue'
import { register } from 'swiper/element/bundle'
import FsLightbox from "fslightbox-vue";
import { truncate } from 'lodash';
import { VCardText } from "vuetify/lib/components/index.mjs"
import AddMediaDrawer from "@/components/Observation/AddMediaDrawer.vue";
import EditObservationDrawer from "@/components/Observation/EditObservationDrawer.vue";
import DeleteDialog from '@/views/general/DeleteDialog.vue'
import {toast} from "vue3-toastify";

definePage({
  meta: {
    layout: 'default',
    role: 'user'
  },
})

const accessToken = ref('pk.eyJ1IjoicmVuYXRvbXMiLCJhIjoiY2pzcDNpaDdlMHFvcTQ5cWx3eXJlMDZ2eiJ9.3GsYRV5ahGFqCpb01u7qFw')
const urlOutdoors = ref('https://api.mapbox.com/styles/v1/mapbox/outdoors-v11/tiles/{z}/{x}/{y}?access_token=' + accessToken.value)
const urlSattelite = ref('https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=' + accessToken.value)

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const uuid = route.params.uuid
const zoom = ref(17)
const observation = ref(null)
const mapRef = ref()
const toggler = ref(false)
const selected = ref([])
const selectedType = ref('image')
const showAddMediaDrawer = ref(false)
const menu = ref(false)
const showDeleteDialog = ref(false)
const showEditObservationDialog = ref(false)
const showDeletePhotoDialog = ref(false)
const selectedPhoto = ref(null)

const bread = ref([
  {
    text: t('observations.title'),
    link: { name: 'observations' }
  },
  {
    text: '...'
  }
])

function onMapReady(m) {
  mapRef.value = m
}

const getData = () => {
  axios.get('/observation/' + uuid).then(response => {
    observation.value = response.data
  }).catch(e => {
    console.error(e)
  })
}

getData()


watch(observation, () => {
  bread.value = [
    {
      text: t('observations.title'),
      link: { name: 'observations' }
    },
    {
      text: truncate(observation.value.title, { length: 20, omission: '...' })
    }
  ]
})

const openLightbox = (val) => {
  if (!val) return
  const src = typeof val === 'string' ? val : val?.optimized ?? val?.url
  if (!src) return

  selected.value = [src]
  nextTick(() => {
    toggler.value = !toggler.value
    selectedType.value = type
  })
}


register()

const getCenter = () => {
  if (observation.value.latitude && observation.value.longitude) {
    return [observation.value.latitude, observation.value.longitude]
  }

  return [
    observation.value.city.latitude,
    observation.value.city.longitude
  ]  
}

const deleteObservation = () => {
  axios.delete('/observation/' + uuid).then(response => {
    toast.success(t('observations.observation_deleted'), {
      autoClose: 3000,
    })
    showDeleteDialog.value = false
    return router.replace({ name: 'observations' })
  }).catch(e => {
    toast.error(t('layout.error'), {
      autoClose: 3000,
    })
    console.error(e)
  })
}

const deleteMedia = () => {
  if (selectedPhoto.value === null) {
    return
  }

  axios.delete('/observation/' + uuid + '/media/' + selectedPhoto.value.uuid).then(response => {
    toast.success(t('observations.media_deleted'), {
      autoClose: 3000,
    })
    showDeletePhotoDialog.value = false
    getData()
  }).catch(e => {
    toast.error(t('layout.error'), {
      autoClose: 3000,
    })
    console.error(e)
  })
}
</script>

<template>
  <div>
    <Breadcrumbs :items="bread"/>

    <Loader
      v-if="observation === null"
      class="mt-4"
    />
    <VRow v-else class="mb-6">

      <VCol
        md="6"
        lg="8"
        cols="12"
      >
        <!-- Details Section -->
        <VCard class="observation-meta mb-4 pa-3" elevation="2" style="border-radius: 16px;">
          <div class="meta-item">
            <VIcon icon="tabler-info-circle" class="meta-icon text-primary" />
            <span class="meta-text">
                {{ $t('observations.types.' + observation.type.slug) }}
            </span>
          </div>
          <div class="meta-separator"></div>
          <div class="meta-item">
            <VIcon icon="tabler-map-pin" class="meta-icon text-primary" />
            <span class="meta-text">
                <strong>{{ observation.city?.name ?? observation.country.name }}</strong>
                <span v-if="observation.city">, {{ observation.country.name }}</span>
              </span>
          </div>

          <div class="meta-separator"></div>

          <div class="meta-item">
            <VIcon icon="tabler-calendar" class="meta-icon text-primary" />
            <span class="meta-text">
                {{ moment.utc(observation.occured_at).local().format('D MMMM YYYY') }}
              </span>
          </div>


        </VCard>


        <VCard
          class="overflow-visible pa-4"
          style="border-radius: 16px;"
          elevation="2"
        >
          <!-- Header -->
          <div class="d-flex justify-space-between align-start mb-2">
            <div>
              <h4 class="text-h5 font-weight-semibold mb-1 text-high-emphasis">
                {{ observation.title }}
              </h4>
            </div>

            <VMenu
              v-if="observation.permissions.edit"
              v-model="menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
            >
              <template #activator="{ props }">
                <VBtn
                  icon
                  size="small"
                  variant="text"
                  color="primary"
                  v-bind="props"
                >
                  <VIcon icon="tabler-adjustments" size="x-large" />
                  <VTooltip activator="parent" location="top">
                    {{ $t('layout.options') }}
                  </VTooltip>
                </VBtn>
              </template>

              <VList>
                <VListItem
                  :title="$t('layout.edit')"
                  prepend-icon="tabler-pencil"
                  @click="showEditObservationDialog = true"
                />
                <VListItem
                  :title="$t('layout.delete')"
                  prepend-icon="tabler-trash"
                  @click="showDeleteDialog = true"
                />
              </VList>
            </VMenu>
          </div>


          <div class="mb-2">
            <div v-if="observation.description" class="d-flex align-center mt-2 mt-sm-0">
              <VIcon icon="tabler-align-left" class="me-1" />
              <span class="text-subtitle-1">{{ observation.description }}</span>
            </div>
          </div>

          <div class="mb-4">
            <swiper-container
              pagination-clickable="true"
              slides-per-view="5"
              space-between="24"
              events-prefix="swiper-"
              :breakpoints="{
                1280: { slidesPerView: 5, spaceBetween: 24 },
                1024: { slidesPerView: 4, spaceBetween: 20 },
                768: { slidesPerView: 3, spaceBetween: 16 },
                640: { slidesPerView: 2, spaceBetween: 12 },
                320: { slidesPerView: 1, spaceBetween: 8 },
              }"
            >
              <swiper-slide
                v-for="(swiperMedia, i) in observation.media"
                :key="i"
              >
                <div v-if="swiperMedia.type === 'image'" class="media-container">
                  <VImg
                    :src="swiperMedia.thumb_url"
                    @click="openLightbox(swiperMedia.original_url, 'image')"
                    class="rounded cursor-pointer"
                  />
                  <div class="media-overlay">
                    <VIcon icon="tabler-photo" class="me-1" />
                  </div>

                  <div v-if="observation.permissions.edit" class="media-overlay-delete">
                    <VBtn
                      icon
                      variant="tonal"
                      color="error"
                      size="x-small"
                      @click="selectedPhoto = swiperMedia; showDeletePhotoDialog = true;"
                    >
                      <VIcon
                        :size="22"
                        icon="tabler-trash"
                      />
                    </VBtn>
                  </div>
                </div>
                <div v-else class="media-container">
                  <VImg
                    :src="swiperMedia.thumb_url"
                    @click="openLightbox(swiperMedia.original_url, 'video')"
                    class="rounded"
                  />
                  <div class="media-overlay">
                    <VIcon icon="tabler-video" class="me-1" />
                  </div>
                  <div v-if="observation.permissions.edit" class="media-overlay-delete">
                    <VBtn
                      icon
                      variant="tonal"
                      color="error"
                      size="x-small"
                      @click="selectedPhoto = swiperMedia; showDeletePhotoDialog = true;"
                    >
                      <VIcon
                        :size="22"
                        icon="tabler-trash"
                      />
                    </VBtn>
                  </div>
                </div>
              </swiper-slide>
              <!-- Add New -->
              <swiper-slide v-if="observation.permissions.edit && observation.media.length < 5"  class="add-media-slide" @click="showAddMediaDrawer = true">
                <div class="media-container flex items-center justify-center border border-dashed border-primary/40 rounded-lg h-[160px] cursor-pointer hover:bg-primary/5 transition-all">
                  <VBtn
                    icon
                    color="primary"
                    variant="tonal"
                    size="small"
                  >
                    <VIcon icon="tabler-plus" size="22" />
                  </VBtn>
                </div>
              </swiper-slide>
            </swiper-container>
          </div>


          <div class="d-flex justify-space-between align-center flex-wrap">
            <div class="d-flex align-center">
              <VAvatar size="34" :image="observation.user.avatar_thumbnail" />
              <span class="ms-2 font-weight-medium">{{ observation.user.name }}</span>
            </div>

            <div class="d-flex align-center">
              <IconBtn icon="tabler-eye" class="me-1" />
              <span class="text-subtitle-2 mt-1">{{ observation.views }}</span>
            </div>
          </div>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        lg="4"
        md="6"
      >
        <VCard 
          class="overflow-visible pa-0"
          style="border-radius: 16px;"
          elevation="2"
          outlined
        >
          <VCardText class="pa-0">
            <div style="height: 100%; width: 100%; border-radius: 8px; overflow: hidden;">
              <LMap
                :zoom="zoom"
                :center="getCenter()"
                :use-global-leaflet="false"
                @ready="onMapReady"
                style="height: 400px; width: 100%;"
              >
                <LMarker :lat-lng="getCenter()" />
                <LFullScreen :map-ref="mapRef" />
                <LTileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  layer-type="base"
                  name="OpenStreetMap"
                  :visible="false"
                />
                <LTileLayer
                  :url="urlSattelite"
                  layer-type="base"
                  name="Mapbox Satellite"
                  visible
                />
                <LTileLayer
                  :url="urlOutdoors"
                  layer-type="base"
                  name="Mapbox Outdoors"
                  :visible="false"
                />
                <LControlLayers
                  :collapsed="false"
                  position="topright"
                />
              </LMap>
            </div>
          </VCardText>
        </VCard>
      </VCol>
      
    </VRow>

    <FsLightbox
      v-if="selected.length"
      :toggler="toggler"
      :sources="[...selected]"
      :type="selectedType"
    />

    <AddMediaDrawer
      v-if="observation && observation.permissions.edit"
      :is-drawer-open="showAddMediaDrawer"
      :observation="observation"
      @update:is-drawer-open="(val) => showAddMediaDrawer = val"
      @submit="getData"
    />
    <DeleteDialog
      :is-dialog-open="showDeleteDialog"
      :title="$t('observations.delete_observation')"
      :text="$t('observations.delete_observation_confirm')"
      @update:is-dialog-open="(val) => showDeleteDialog = val"
      @submit="deleteObservation"
    />
    <DeleteDialog
      :is-dialog-open="showDeletePhotoDialog"
      :title="$t('observations.delete_media')"
      :text="$t('observations.delete_media_confirm')"
      @update:is-dialog-open="(val) => showDeletePhotoDialog = val"
      @submit="deleteMedia"
    />
    <EditObservationDrawer
      v-if="observation && observation.permissions.edit"
      :observation="observation"
      :is-drawer-open="showEditObservationDialog"
      @update:is-drawer-open="(val) => showEditObservationDialog = val"
      @submit="getData"
    />
  </div>
</template>


<style scoped>
.observation-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.meta-icon {
  opacity: 0.8;
}

.meta-text {
  font-size: 0.95rem;
  color: var(--v-theme-on-surface);
}

.meta-separator {
  width: 1px;
  height: 20px;
  background-color: rgba(0, 0, 0, 0.1);
}


.media-container {
  position: relative;
}

.media-overlay-delete {
  position: absolute;
  top: 2px;
  right: 2px;
  color: white;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.media-overlay {
  position: absolute;
  bottom: 2px;
  right: 2px;
  color: white;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Add-media slide */
.add-media-slide .media-container {
  border: 2px dashed rgba(59, 130, 246, 0.3);
  background: rgba(59, 130, 246, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
  transition: background 0.25s ease, border-color 0.25s ease;
}

.add-media-slide .media-container:hover {
  border-color: rgba(59, 130, 246, 0.6);
  background: rgba(59, 130, 246, 0.1);
}
</style>
